import { Link } from '@tanstack/react-router';
import { CreditCard, Pencil, Receipt, Tags } from 'lucide-react';
import { useState } from 'react';

import { useGetParentById } from '~entities/parent/api/get-parent-by-id';
import { useCurrentUserRole } from '~entities/users/hooks/use-current-user-role';

import { EditParentForm } from '~features/contacts/parent/parent-form';
import { ParentTags } from '~features/parents/ui/parent-tags';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';

/**
 * Day-to-day parent ops that used to live on the classic family page
 * (ENR-4054 ship-risk: tags, edit profile, transactions, payment methods).
 */
export const FamilyHubQuickActions = ({
  billingParentId,
}: {
  billingParentId: string;
}) => {
  const { hasAdminAccess, isRolesReady } = useCurrentUserRole();
  const canViewFamilyBilling = isRolesReady && hasAdminAccess;
  const { data: parent } = useGetParentById(billingParentId);
  const [editOpen, setEditOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);

  const parentName =
    `${parent?.firstName ?? ''} ${parent?.lastName ?? ''}`.trim() ||
    parent?.name ||
    'Parent';

  return (
    <>
      <div className='mt-6 flex flex-wrap gap-2'>
        <Button
          type='button'
          variant='secondary'
          size='sm'
          onClick={() => {
            setEditOpen(true);
          }}
          disabled={!parent}
        >
          <Pencil className='size-4' />
          Edit Profile
        </Button>
        <Button
          type='button'
          variant='secondary'
          size='sm'
          onClick={() => {
            setTagsOpen(true);
          }}
        >
          <Tags className='size-4' />
          Parent Tags
        </Button>
        {canViewFamilyBilling ? (
          <Button asChild variant='secondary' size='sm'>
            <Link
              to='/parents/transactions-table'
              search={{ parentId: billingParentId }}
            >
              <Receipt className='size-4' />
              Total Transactions
            </Link>
          </Button>
        ) : null}
        <Button asChild variant='secondary' size='sm'>
          <Link
            to='/parents/$parentId/saved-payment-methods'
            params={{ parentId: billingParentId }}
          >
            <CreditCard className='size-4' />
            Payment Details
          </Link>
        </Button>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className='sm:max-w-[425px]'>
          <DialogHeader>
            <DialogTitle>Edit profile</DialogTitle>
            <DialogDescription>
              Update this guardian&apos;s profile. Click save when you&apos;re
              done.
            </DialogDescription>
          </DialogHeader>
          {parent ? (
            <EditParentForm
              // Empty studentId → PATCH /parents/:id (billing guardian).
              // A studentId would hit updateParentAllDetails and rewrite
              // student.parentId instead (ENR-4054 smoke cross-write).
              studentId=''
              parent={parent}
              onCancel={() => {
                setEditOpen(false);
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      {tagsOpen ? (
        <ParentTags
          open={tagsOpen}
          onOpenChange={setTagsOpen}
          parentId={billingParentId}
          parentName={parentName}
          isDemoParent={parent?.isDemoData === true}
        />
      ) : null}
    </>
  );
};
